import { Timestamps, Versioned } from './shared.types';

/** Banner colors (readable on mobile and in dark mode). */
export enum AnnouncementColor {
  ORANGE = 'ORANGE',
  RED = 'RED',
  BLUE = 'BLUE',
  GREEN = 'GREEN',
  PURPLE = 'PURPLE',
}

/** Who sees an announcement. */
export enum AnnouncementScope {
  ALL = 'ALL',
  MERCHANTS = 'MERCHANTS',
  STORES = 'STORES',
}

/** The message in every app language; all three are required. */
export interface AnnouncementMessages {
  en: string;
  pt: string;
  fr: string;
}

/**
 * A banner platform admins show in the web apps (service-admin). Live
 * from `starts_at` until `ends_at`, or until an admin ends it.
 */
export interface Announcement extends Timestamps, Versioned {
  id: string;
  messages: AnnouncementMessages;
  color: AnnouncementColor;
  scope: AnnouncementScope;
  /**
   * With scope MERCHANTS: every store of these merchants. With STORES: the
   * merchants of the chosen stores (display only).
   */
  merchant_ids: string[];
  /** With scope STORES: only these stores. */
  store_ids: string[];
  /** ISO time it starts showing. */
  starts_at: string;
  /** ISO time it stops showing. */
  ends_at: string;
  /** Set when an admin ended it early. */
  ended_at?: string | null;
}

/** `POST /admin/announcements` (admin session). */
export interface CreateAnnouncement {
  messages: AnnouncementMessages;
  color: AnnouncementColor;
  scope: AnnouncementScope;
  merchant_ids?: string[] | null;
  store_ids?: string[] | null;
  /** Missing = now. */
  starts_at?: string | null;
  ends_at: string;
}

/** `PUT /admin/announcements/{id}`: the whole announcement. */
export interface UpdateAnnouncement extends CreateAnnouncement {
  /** The version read; a stale one is refused (409). */
  version: number;
}

/** `GET /admin/announcements` (admin session), newest first. */
export interface AnnouncementsResponse {
  announcements: Announcement[];
}

/** One live announcement as the apps get it (no actor fields). */
export interface ActiveAnnouncement {
  id: string;
  /** Changes on every edit: a user's dismissal applies to one version. */
  version: number;
  messages: AnnouncementMessages;
  color: AnnouncementColor;
}

/** `GET /admin/announcements/active?merchant_id=&store_id=` (public). */
export interface GetActiveAnnouncements {
  merchant_id?: string | null;
  store_id?: string | null;
}

export interface ActiveAnnouncementsResponse {
  announcements: ActiveAnnouncement[];
}

export interface AnnouncementDBItem extends Announcement {
  PK: string;
  SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
}
